import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CubeAgentConfig,
  getCubeAgentConfigFromRemarks,
  isCubeAgentConfigComplete,
  persistCubeDetailsToRemarks,
} from './cube-dialer-mode';

interface CubeAgentConfigResponse {
  resultShort?: string;
  cubeDetails?: Record<string, unknown>;
  agentID?: string;
}

/**
 * Loads AgentId/CampaignId from API into localStorage remarks.cubeDetails for login.
 */
@Injectable({ providedIn: 'root' })
export class CubeAgentConfigService {
  private loadPromise: Promise<CubeAgentConfig | null> | null = null;

  constructor(private http: HttpClient) {}

  async ensureLoaded(forceRefresh = false): Promise<CubeAgentConfig | null> {
    if (!forceRefresh) {
      const cached = getCubeAgentConfigFromRemarks();
      if (isCubeAgentConfigComplete(cached)) {
        return cached;
      }
    }
    if (!forceRefresh && this.loadPromise) {
      return this.loadPromise;
    }
    this.loadPromise = this.fetchFromDb();
    try {
      return await this.loadPromise;
    } finally {
      this.loadPromise = null;
    }
  }

  private async fetchFromDb(): Promise<CubeAgentConfig | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<CubeAgentConfigResponse>(`${environment.api_url}/api/cube/agent-config`)
      );
      if (res?.resultShort === 'success' && res.cubeDetails) {
        persistCubeDetailsToRemarks(res.cubeDetails, res.agentID);
        const agent = getCubeAgentConfigFromRemarks();
        console.log('[CubeAgentConfig] Loaded from DB', {
          agentId: agent?.agentId,
          campaignId: agent?.campaignId,
          extension: agent?.extension,
          hasPassword: !!agent?.password,
        });
        return agent;
      }
      console.warn('[CubeAgentConfig] DB response missing cubeDetails', res);
    } catch (error) {
      console.error('[CubeAgentConfig] Failed to load from DB', error);
    }
    return getCubeAgentConfigFromRemarks();
  }
}
